import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Copy, Users, Check, Share2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

export const ReferralSection = () => {
  const [referralCode, setReferralCode] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyStatus, setApplyStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`, { withCredentials: true }),
        axios.get(`${API}/referral/stats`, { withCredentials: true })
      ]);
      setReferralCode(codeRes.data.referral_code);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnWhatsApp = () => {
    const message = `Join Nakshatra - India's #1 Vedic Astrology app! Get personalized horoscopes, Kundli matching & AI predictions. Use my referral code: ${referralCode} for exclusive benefits! 🌟\n\n${window.location.origin}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const applyReferralCode = async () => {
    if (!applyCode.trim()) return;
    
    try {
      const response = await axios.post(
        `${API}/referral/apply`,
        { referral_code: applyCode.trim().toUpperCase() },
        { withCredentials: true }
      );
      setApplyStatus({ type: 'success', message: response.data.message });
      setTimeout(() => {
        setShowApplyModal(false);
        setApplyCode('');
        setApplyStatus({ type: '', message: '' });
      }, 2000);
    } catch (error) {
      setApplyStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to apply code' 
      });
    }
  };

  if (loading) {
    return (
      <div className="cosmic-card p-6 animate-pulse">
        <div className="h-6 bg-[#FCE9D9]/70 rounded w-1/3 mb-4"></div>
        <div className="h-12 bg-[#FCE9D9]/70 rounded mb-4"></div>
        <div className="h-20 bg-[#FCE9D9]/70 rounded"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="cosmic-card p-6"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 flex items-center justify-center">
          <Gift className="w-5 h-5 text-[#C84B32]" />
        </div>
        <div>
          <h3 className="font-['Fraunces'] text-xl text-white">Refer & Earn</h3>
          <p className="text-[#6B6560] text-sm">Get 30 days free for each friend who subscribes</p>
        </div>
      </div>

      {/* Referral Code Display */}
      <div className="bg-[#FCE9D9]/40 rounded-xl p-4 mb-6">
        <p className="text-[#6B6560] text-xs mb-2">Your Referral Code</p>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-white rounded-lg px-4 py-3 font-mono text-xl text-[#C84B32] tracking-wider text-center">
            {referralCode}
          </div>
          <Button
            onClick={copyCode}
            variant="outline"
            className="border-[#C84B32]/30 hover:bg-[#C84B32]/10"
            data-testid="copy-referral-btn"
          >
            {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5 text-[#C84B32]" />}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 bg-[#FCE9D9]/40 rounded-xl">
          <p className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</p>
          <p className="text-[#6B6560] text-xs">Invited</p>
        </div>
        <div className="text-center p-3 bg-[#FCE9D9]/40 rounded-xl">
          <p className="text-2xl font-bold text-green-600">{stats?.successful_referrals || 0}</p>
          <p className="text-[#6B6560] text-xs">Subscribed</p>
        </div>
        <div className="text-center p-3 bg-[#FCE9D9]/40 rounded-xl">
          <p className="text-2xl font-bold text-[#C84B32]">{(stats?.rewards_earned || 0) * 30}</p>
          <p className="text-[#6B6560] text-xs">Days Earned</p>
        </div>
      </div>

      {/* Share Button */}
      <Button
        onClick={shareOnWhatsApp}
        className="w-full bg-green-600 hover:bg-green-700 text-white mb-4"
        data-testid="share-whatsapp-btn"
      >
        <Share2 className="w-4 h-4 mr-2" />
        Share on WhatsApp
      </Button>

      {/* Apply Code Button */}
      <Button
        onClick={() => setShowApplyModal(true)}
        variant="outline"
        className="w-full border-[#282622]/15 text-[#6B6560] hover:text-white hover:bg-[#FCE9D9]/60"
        data-testid="apply-code-btn"
      >
        Have a referral code? Apply here
      </Button>

      {/* Apply Code Modal */}
      {showApplyModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowApplyModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="cosmic-card w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-xl font-semibold text-white">Apply Referral Code</h4>
              <button onClick={() => setShowApplyModal(false)} className="text-[#6B6560] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {applyStatus.message && (
              <div className={`mb-4 p-3 rounded-lg ${
                applyStatus.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/20 text-green-600' 
                  : 'bg-red-500/10 border border-red-500/20 text-red-600'
              }`}>
                {applyStatus.message}
              </div>
            )}

            <Input
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="Enter referral code"
              className="bg-white border-[#282622]/10 text-white mb-4 text-center font-mono text-lg tracking-wider"
              data-testid="apply-code-input"
            />

            <Button
              onClick={applyReferralCode}
              disabled={!applyCode.trim()}
              className="w-full cosmic-btn"
              data-testid="submit-referral-btn"
            >
              Apply Code
            </Button>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ReferralSection;
